import type { ReactNode } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import { ViewState } from "../types/alerting";

interface CursorDataTableProps<T> {
  state: ViewState;
  rows: T[];
  dataKey: string;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  emptyMessage: string;
  refusedMessage: string;
  failedMessage?: string;
  error?: string | null;
  rowClassName?: (row: T) => string;
  children: ReactNode;
}

/**
 * A table over cursor-paged data: no paginator, a load-more control, and the
 * four non-content states kept visibly distinct from one another.
 */
function CursorDataTable<T>({
  state,
  rows,
  dataKey,
  hasMore,
  loadingMore,
  onLoadMore,
  emptyMessage,
  refusedMessage,
  failedMessage = "Could not load this data",
  error = null,
  rowClassName,
  children,
}: CursorDataTableProps<T>) {
  if (state === "loading") {
    return (
      <div className="flex align-items-center gap-3 py-5" data-testid="view-state-loading">
        <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
        <span className="text-color-secondary">Loading…</span>
      </div>
    );
  }

  if (state === "refused") {
    return (
      <div className="py-3" data-testid="view-state-refused">
        <Message severity="warn" text={refusedMessage} />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="py-3" data-testid="view-state-failed">
        <Message severity="error" text={error ? `${failedMessage}: ${error}` : failedMessage} />
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className="py-3" data-testid="view-state-empty">
        <Message severity="info" text={emptyMessage} />
      </div>
    );
  }

  return (
    <div data-testid="view-state-ready">
      <DataTable
        value={rows as any[]}
        dataKey={dataKey}
        paginator={false}
        className="p-datatable-sm"
        rowClassName={rowClassName as any}
      >
        {children}
      </DataTable>

      {error && (
        <div className="mt-3" data-testid="load-more-error">
          <Message severity="error" text={`Could not load more: ${error}`} />
        </div>
      )}

      {hasMore && (
        <div className="flex justify-content-center mt-3">
          <Button
            label="Load more"
            icon="pi pi-angle-down"
            outlined
            loading={loadingMore}
            disabled={loadingMore}
            onClick={onLoadMore}
          />
        </div>
      )}
    </div>
  );
}

export default CursorDataTable;
